import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of, tap, timer, switchMap } from 'rxjs';
import { openDB, IDBPDatabase } from 'idb';
import { environment } from '@ui-nuvertos/shared';
import { authConfig } from './auth.config';

interface AuthDB {
  'auth-store': {
    key: string;
    value: any;
  };
}

interface User {
  id: string;
  email: string;
  token: string;
  refreshToken: string;
  expiresAt: number;
  center_id?: number;
}

export interface CenterInfo {
  id: number;
  code: string;
  name: string;
  location?: string;
  description?: string;
  is_active: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();
  private sessionTimeout: number = 30 * 60 * 1000; // 30 minutes
  private refreshTimer?: any;

  private dbPromise: Promise<IDBPDatabase<AuthDB>>;
  private http = inject(HttpClient);
  
  constructor() {
    this.dbPromise = openDB<AuthDB>('auth-db', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('auth-store')) {
          db.createObjectStore('auth-store');
        }
      }
    });
    
    this.initializeSession();
  }

  private initializeSession(): void {
    this.getAuthData('user').then(user => {
      if (user && this.isTokenValid(user)) {
        this.currentUserSubject.next(user);
        this.startSessionTimer();
      } else {
        this.clearAuthData();
      }
    });
  }

  private isTokenValid(user: User): boolean {
    return user.expiresAt > Date.now();
  }

  private startSessionTimer(): void {
    // Only start timer if frontend session timeout is enabled
    if (!authConfig.enableSessionTimeout) {
      return;
    }
    
    this.refreshTimer = timer(this.sessionTimeout).pipe(
      switchMap(() => this.refreshToken())
    ).subscribe({
      next: (response) => {
        if (response) {
          this.startSessionTimer(); // Restart timer
        } else {
          this.logout();
        }
      },
      error: () => this.logout()
    });
  }

  loadUserSession(): Observable<User | null> {
    return this.http.get<User>(`${this.apiUrl}/auth/authenticate`, { withCredentials: true }).pipe(
      tap(user => {
        if (user) {
          this.currentUserSubject.next(user);
          this.saveAuthData('user', user);
          this.startSessionTimer();
        }
      }),
      catchError(() => {
        this.currentUserSubject.next(null);
        return of(null);
      })
    );
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/login`, { email, password }, { withCredentials: true })
      .pipe(
        tap((response) => {
          if (response.user) {
            const user: User = {
              ...response.user,
              expiresAt: Date.now() + (response.expiresIn * 1000)
            };
            this.saveAuthData('user', user);
            this.currentUserSubject.next(user);
            this.startSessionTimer();
          }
        })  
      );
  }

  refreshToken(): Observable<any> {
    const user = this.currentUserSubject.value;
    if (!user?.refreshToken) {
      return of(null);
    }

    return this.http.post<any>(`${this.apiUrl}/auth/refresh`, { refreshToken: user.refreshToken })
      .pipe(
        tap((response) => {
          if (response.user) {
            const updatedUser: User = {
              ...response.user,
              expiresAt: Date.now() + (response.expiresIn * 1000)
            };
            this.saveAuthData('user', updatedUser);
            this.currentUserSubject.next(updatedUser);
          }
        }),
        catchError(() => {
          this.logout();
          return of(null);
        })
      );
  }

  logout(): void {
    this.currentUserSubject.next(null);
    this.clearAuthData();
    this.stopSessionTimer();
    
    // Call logout API
    this.http.post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .subscribe();
  }

  private stopSessionTimer(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
      this.refreshTimer = undefined;
    }
  }

  async saveAuthData(key: string, value: any): Promise<void> {
    const db = await this.dbPromise;
    await db.put('auth-store', value, key);
  }

  async clearAuthData(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear('auth-store');
  }

  async getAuthData(key: string): Promise<any> {
    const db = await this.dbPromise;
    return db.get('auth-store', key);
  }

  isAuthenticated(): boolean {
    const user = this.currentUserSubject.value;
    return user !== null && this.isTokenValid(user);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Save center information to IndexedDB
   */
  async saveCenterInfo(center: CenterInfo): Promise<void> {
    await this.saveAuthData('center', center);
  }

  /**
   * Get center information from IndexedDB
   */
  async getCenterInfo(): Promise<CenterInfo | null> {
    return await this.getAuthData('center');
  }

  /**
   * Clear center information from IndexedDB
   */
  async clearCenterInfo(): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('auth-store', 'center');
  }
}